import React, { useCallback, useMemo, useRef, useState } from 'react';
import Toast from '../Toast';
import ToastContext from './ToastContext';

const DEFAULT_DURATION = 2400;
const MAX_VISIBLE = 3;
const LEAVE_MS = 220; // matches CSS leave keyframe

let idCounter = 0;
const nextId = () => {
  idCounter += 1;
  return `t-${Date.now()}-${idCounter}`;
};

/**
 * <ToastProvider> wraps the app. Pairs with the useToast() hook.
 *
 * Visible toasts: up to MAX_VISIBLE (3). Additional showToast() calls queue
 * and surface as visible slots free up. Each toast auto-dismisses after
 * `duration` ms (default 2400) but can be dismissed early via tap/click.
 */
const ToastProvider = ({ children }) => {
  // Visible toast queue. Each entry: { id, message, duration, leaving }.
  const [visible, setVisible] = useState([]);
  // Pending queue when we exceed MAX_VISIBLE.
  const pendingRef = useRef([]);
  // Active timers keyed by toast id so we can clear on manual dismiss.
  const timersRef = useRef(new Map());

  const clearTimer = useCallback((id) => {
    const t = timersRef.current.get(id);
    if (t) {
      clearTimeout(t);
      timersRef.current.delete(id);
    }
  }, []);

  const promote = useCallback(() => {
    if (pendingRef.current.length === 0) return;
    setVisible((cur) => {
      if (cur.length >= MAX_VISIBLE) return cur;
      const next = pendingRef.current.shift();
      return next ? [...cur, next] : cur;
    });
  }, []);

  const remove = useCallback(
    (id) => {
      clearTimer(id);
      setVisible((cur) => cur.filter((t) => t.id !== id));
      // After paint, try to surface a pending toast.
      setTimeout(promote, 0);
    },
    [clearTimer, promote],
  );

  const beginLeave = useCallback(
    (id) => {
      clearTimer(id);
      setVisible((cur) => cur.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
      const tid = setTimeout(() => remove(id), LEAVE_MS);
      timersRef.current.set(id, tid);
    },
    [clearTimer, remove],
  );

  const scheduleDismiss = useCallback(
    (id, duration) => {
      clearTimer(id);
      const tid = setTimeout(() => beginLeave(id), duration);
      timersRef.current.set(id, tid);
    },
    [beginLeave, clearTimer],
  );

  const showToast = useCallback(
    (message, opts = {}) => {
      const duration = typeof opts.duration === 'number' ? opts.duration : DEFAULT_DURATION;
      const item = { id: nextId(), message, duration, leaving: false };

      setVisible((cur) => {
        if (cur.length >= MAX_VISIBLE) {
          pendingRef.current.push(item);
          return cur;
        }
        // Schedule dismiss for the just-added item after paint.
        setTimeout(() => scheduleDismiss(item.id, duration), 0);
        return [...cur, item];
      });

      return item.id;
    },
    [scheduleDismiss],
  );

  const dismissToast = useCallback(
    (id) => {
      // Manual dismiss is immediate (matches contract: "manual dismiss removes immediately").
      remove(id);
    },
    [remove],
  );

  // When visible becomes < MAX, promote pending. Also start timers for any
  // newly added items that don't have one yet (covers promote() flow).
  React.useEffect(() => {
    visible.forEach((t) => {
      if (!t.leaving && !timersRef.current.has(t.id)) {
        scheduleDismiss(t.id, t.duration);
      }
    });
  }, [visible, scheduleDismiss]);

  // Clear all timers on unmount.
  React.useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((tid) => clearTimeout(tid));
      timers.clear();
    };
  }, []);

  const value = useMemo(() => ({ showToast, dismissToast }), [showToast, dismissToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="glacier-toast-region" aria-live="polite" aria-atomic="false">
        {visible.map((t) => (
          <Toast key={t.id} leaving={t.leaving} onDismiss={() => dismissToast(t.id)}>
            {t.message}
          </Toast>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
